import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  ShieldCheck,
  Truck,
  ClipboardCheck,
  Clock,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHero, GoldButton, SectionEyebrow, FadedBackdrop } from "@/components/ui-bits";
import { VENDOR } from "@/constants/testIds";
import { IMG } from "@/constants/images";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  "Title Officer",
  "Escrow Officer",
  "Loan Officer / Mortgage Broker",
  "Signing Service Manager",
  "Real Estate Attorney",
  "Other",
];

const VOLUMES = [
  "1–5 signings / month",
  "6–15 signings / month",
  "16–30 signings / month",
  "30+ signings / month",
  "Project / one-off",
];

const BENEFITS = [
  {
    icon: ClipboardCheck,
    title: "Pre-Audited Packages",
    body: "Every loan packet is reviewed page-by-page before the signer arrives. Tabs, witnesses, and IDs confirmed in advance.",
  },
  {
    icon: ShieldCheck,
    title: "Vendor-Ready Credentials",
    body: "Florida-commissioned, bonded, E&O insured, NNA-trained, and background-screened. Documents ready for your vendor onboarding packet.",
  },
  {
    icon: Truck,
    title: "Same-Day Drop-Backs",
    body: "Scan-backs and tracked shipping per your closing instructions, with full drop confirmation before we leave the location.",
  },
  {
    icon: Clock,
    title: "Nights, Weekends, Rush",
    body: "We schedule around your closing calendar. Evening, weekend, and rush appointments accepted with up-front pricing.",
  },
];

const inputBase =
  "w-full bg-[#1A2B45] border border-white/10 text-white p-4 text-sm focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] outline-none transition-colors rounded-none placeholder:text-slate-500";

const triggerBase =
  "bg-[#1A2B45] border-white/10 text-white p-4 h-auto text-sm rounded-none focus:ring-1 focus:ring-[#D4AF37] focus:border-[#D4AF37] [&>span]:text-left";

export default function VendorPage() {
  const [form, setForm] = React.useState({
    company_name: "",
    contact_name: "",
    role: "",
    email: "",
    phone: "",
    website: "",
    coverage_area: "",
    expected_volume: "",
    notes: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState("");

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (
      !form.company_name.trim() ||
      !form.contact_name.trim() ||
      !form.role ||
      !form.email.trim() ||
      !form.phone.trim()
    ) {
      setError("Please complete the required fields marked with *.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/vendor-signup`, {
        company_name: form.company_name.trim(),
        contact_name: form.contact_name.trim(),
        role: form.role,
        email: form.email.trim(),
        phone: form.phone.trim(),
        website: form.website.trim() || null,
        coverage_area: form.coverage_area.trim() || null,
        expected_volume: form.expected_volume || null,
        notes: form.notes.trim() || null,
      });
      setSuccess(true);
      setForm({
        company_name: "",
        contact_name: "",
        role: "",
        email: "",
        phone: "",
        website: "",
        coverage_area: "",
        expected_volume: "",
        notes: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't submit your request. Please try again or email us directly.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section
        data-testid={VENDOR.hero}
        className="relative overflow-hidden border-b border-white/10"
      >
        <FadedBackdrop src={IMG.realEstateSigning} opacity={0.28} />
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#142339] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 sm:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <SectionEyebrow className="mb-6">
                Vendor Onboarding · For Title, Escrow & Mortgage Pros
              </SectionEyebrow>
              <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tighter">
                Add OGO Notary to your{" "}
                <span className="text-[#D4AF37]">vendor list</span> — and close
                cleaner.
              </h1>
              <p className="mt-8 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed">
                Built for repeat B2B closings. One short form gets you a
                vetted, bonded, NNA-trained signing agent with audit-grade
                packets, same-day scan-backs, and after-hours coverage —
                ready for your next assignment.
              </p>
            </div>
            <div className="lg:col-span-4 hidden lg:block">
              <div className="border border-white/10 bg-[#1A2B45]/80 backdrop-blur-md p-8">
                <div className="eyebrow mb-3">What You Receive</div>
                <ul className="space-y-3 text-sm text-slate-200">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />
                    W-9, commission, bond & E&O on request
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />
                    Background-screening certificate
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />
                    NNA certification & annual training proof
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />
                    Flat-fee package rate sheet
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits + Form */}
      <section className="relative overflow-hidden py-24 sm:py-32">
        <FadedBackdrop src={IMG.signingTable} opacity={0.2} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionEyebrow className="mb-4">Why Add Us</SectionEyebrow>
            <h2 className="font-serif-display text-4xl text-white leading-tight tracking-tight">
              A signing agent your processors won't have to babysit.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              You shouldn't have to chase down missing initials or wait until
              Monday to fund a Friday close. Add OGO Notary and get a
              predictable, audit-ready outcome on every package.
            </p>

            <div className="mt-10 space-y-5">
              {BENEFITS.map((b, i) => (
                <div
                  key={i}
                  data-testid={`vendor-benefit-${i}`}
                  className="flex items-start gap-4 border border-white/10 bg-[#1A2B45] p-5"
                >
                  <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0">
                    <b.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-serif-display text-xl text-white mb-1">
                      {b.title}
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {b.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid={VENDOR.form}
              onSubmit={onSubmit}
              className="border border-[#D4AF37]/40 bg-[#1A2B45] p-8 sm:p-12 relative"
              noValidate
            >
              <div className="absolute -top-3 left-8 bg-[#D4AF37] text-[#142339] text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1">
                Free Onboarding
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <SectionEyebrow>One Form. Vetted Vendor.</SectionEyebrow>
                  <h3 className="font-serif-display text-2xl text-white mt-1">
                    Add OGO Notary to your vendor list
                  </h3>
                </div>
              </div>

              {success && (
                <div
                  data-testid={VENDOR.successAlert}
                  className="mb-6 flex items-start gap-3 border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                  <div className="text-sm text-slate-100">
                    Received. We'll send our W-9, commission, bond, E&O, and
                    rate sheet to the email you provided — typically within
                    one business day.
                  </div>
                </div>
              )}

              {error && (
                <div
                  data-testid={VENDOR.errorAlert}
                  className="mb-6 flex items-start gap-3 border border-red-500/40 bg-red-500/10 p-4"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                  <div className="text-sm text-slate-100">{error}</div>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Company Name" required>
                  <input
                    data-testid={VENDOR.companyName}
                    type="text"
                    value={form.company_name}
                    onChange={onChange("company_name")}
                    className={inputBase}
                    placeholder="Acme Title & Escrow, LLC"
                    required
                  />
                </Field>
                <Field label="Your Name" required>
                  <input
                    data-testid={VENDOR.contactName}
                    type="text"
                    value={form.contact_name}
                    onChange={onChange("contact_name")}
                    className={inputBase}
                    placeholder="Jane Smith"
                    required
                  />
                </Field>
                <Field label="Role" required>
                  <Select
                    value={form.role}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, role: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid={VENDOR.roleTrigger}
                      className={triggerBase}
                    >
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent
                      data-testid={VENDOR.role}
                      className="bg-[#1A2B45] border border-white/10 rounded-none text-white"
                    >
                      {ROLES.map((r) => (
                        <SelectItem
                          key={r}
                          value={r}
                          data-testid={`vendor-role-${r
                            .toLowerCase()
                            .replace(/[^a-z0-9]+/g, "-")
                            .replace(/^-|-$/g, "")}`}
                          className="rounded-none focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                        >
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Work Email" required>
                  <input
                    data-testid={VENDOR.email}
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    className={inputBase}
                    placeholder="you@company.com"
                    required
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    data-testid={VENDOR.phone}
                    type="tel"
                    value={form.phone}
                    onChange={onChange("phone")}
                    className={inputBase}
                    placeholder="(555) 555-0123"
                    required
                  />
                </Field>
                <Field label="Company Website">
                  <input
                    data-testid={VENDOR.website}
                    type="text"
                    value={form.website}
                    onChange={onChange("website")}
                    className={inputBase}
                    placeholder="https://"
                  />
                </Field>
                <Field label="Coverage Area Needed">
                  <input
                    data-testid={VENDOR.coverage}
                    type="text"
                    value={form.coverage_area}
                    onChange={onChange("coverage_area")}
                    className={inputBase}
                    placeholder="e.g., Coastal FL, Brevard County"
                  />
                </Field>
                <Field label="Expected Volume">
                  <Select
                    value={form.expected_volume}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, expected_volume: v }))
                    }
                  >
                    <SelectTrigger
                      data-testid={VENDOR.volumeTrigger}
                      className={triggerBase}
                    >
                      <SelectValue placeholder="Select estimated volume" />
                    </SelectTrigger>
                    <SelectContent
                      data-testid={VENDOR.volume}
                      className="bg-[#1A2B45] border border-white/10 rounded-none text-white"
                    >
                      {VOLUMES.map((v) => (
                        <SelectItem
                          key={v}
                          value={v}
                          className="rounded-none focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]"
                        >
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Notes (package types, vendor portal, certs needed)">
                  <textarea
                    data-testid={VENDOR.notes}
                    value={form.notes}
                    onChange={onChange("notes")}
                    className={`${inputBase} min-h-[120px] resize-y`}
                    placeholder="Anything we should know to get onboarded quickly."
                  />
                </Field>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-slate-500 max-w-md leading-relaxed">
                  We'll respond within one business day with our credentials
                  packet and rate sheet. We never sell your information.
                </p>
                <GoldButton
                  type="submit"
                  data-testid={VENDOR.submit}
                  disabled={submitting}
                  className={submitting ? "opacity-60 cursor-not-allowed" : ""}
                >
                  {submitting ? "Submitting…" : "Add OGO to Vendor List"}
                  {!submitting && <ArrowUpRight className="w-4 h-4" />}
                </GoldButton>
              </div>
            </form>

            <p className="text-xs text-slate-500 leading-relaxed mt-6">
              Prefer a quick general inquiry? Use the{" "}
              <Link to="/contact" className="text-[#D4AF37] hover:underline">
                Contact form
              </Link>{" "}
              instead — this page is optimized for B2B vendor onboarding.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.22em] text-slate-400 mb-2">
        {label}
        {required && <span className="text-[#D4AF37] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
